import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { APP_URL } from '@/data/marketingContent';
import { AgentConsole } from './AgentConsole';
import { EASE } from './primitives';
import { SKIP_ENTRANCE } from './motionPrefs';

const rotating = ['screens every application', 'runs structured AI interviews', 'hands you the shortlist'];
const assurances = ['Free plan with one active role', 'AI video interviews on Pro', 'Recruiters keep every decision'];

export function HomeHero() {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => setIndex(i => (i + 1) % rotating.length), 2600);
    return () => window.clearInterval(id);
  }, [reduced]);

  const enter = (delay: number) => ({ initial: reduced || SKIP_ENTRANCE ? false as const : { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay, ease: EASE } });

  return (
    <section className="relative overflow-hidden bg-bv-base pb-20 pt-32 sm:pt-36 lg:pb-28 lg:pt-44" aria-labelledby="hero-h1">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="bv-hero-grid absolute inset-0" />
        <div className="absolute -top-56 left-[4%] h-[560px] w-[560px] animate-aurora rounded-full bg-[hsl(var(--blue)/0.16)] blur-[110px]" />
        <div className="absolute -top-32 right-[2%] h-[520px] w-[620px] animate-aurora rounded-full bg-[hsl(var(--hero-highlight-start)/0.17)] blur-[120px] [animation-delay:-7s]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-bv-base" />
      </div>

      <div className="relative mx-auto grid max-w-[1240px] grid-cols-1 items-center gap-16 px-5 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:gap-12 lg:px-8">
        <div className="min-w-0">
          <motion.div {...enter(0)}>
            <Link to="/mcp" className="group inline-flex max-w-full items-center gap-2 rounded-full border border-bv-blue-line bg-white/80 py-1 pl-1 pr-3 text-[13px] font-medium text-bv-ink2 shadow-sm backdrop-blur transition hover:border-bv-blue/40 hover:text-bv-ink">
              <span className="bv-btn-gradient shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white">New</span>
              <span className="truncate">Run your hiring from Claude, ChatGPT or Gemini</span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>

          <motion.h1 id="hero-h1" initial={reduced || SKIP_ENTRANCE ? false : { y: 20 }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="mt-7 max-w-[640px] font-display text-[44px] font-semibold leading-[0.98] tracking-[-0.04em] text-bv-ink sm:text-[64px] lg:text-[74px]">
            The <span className="bv-hero-highlight">AI recruiting agent</span> for recruitment agencies
          </motion.h1>

          <motion.div {...enter(0.15)} className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-1 text-lg font-semibold text-bv-ink sm:flex-nowrap sm:text-xl">
            <span className="sr-only">Your agent screens every application, runs structured AI interviews, and hands you the shortlist.</span>
            <span aria-hidden className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bv-ink text-white sm:flex"><Play className="h-3.5 w-3.5 fill-current" /></span>
            <span aria-hidden className="shrink-0">Your agent</span>
            <span aria-hidden className="relative inline-flex h-8 w-full min-w-0 overflow-hidden sm:w-auto sm:min-w-[300px] sm:flex-1">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span key={index} initial={{ y: '100%', opacity: 0 }} animate={{ y: '0%', opacity: 1 }} exit={{ y: '-100%', opacity: 0 }} transition={{ duration: 0.45, ease: EASE }} className="absolute left-0 top-0 whitespace-nowrap leading-8 text-bv-blue">
                  {rotating[index]}
                </motion.span>
              </AnimatePresence>
            </span>
          </motion.div>

          <motion.p {...enter(0.22)} className="mt-6 max-w-[560px] text-[17px] leading-[1.7] text-bv-ink2 sm:text-lg">
            Beatview reads every application against your role criteria, interviews candidates with structured video questions, and hands your recruiters a scored shortlist with the evidence attached. Your team spends its time placing candidates — and still makes every decision.
          </motion.p>

          <motion.div {...enter(0.3)} className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="bv-shine h-[54px] rounded-xl px-7 text-base font-semibold">
              <a href={APP_URL}>Start for free <ArrowRight className="h-4 w-4" /></a>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-[54px] rounded-xl border-bv-blue-line bg-white/80 px-6 text-base font-semibold text-bv-ink backdrop-blur hover:bg-white hover:text-bv-ink">
              <a href="#workflow-demo"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-bv-ink text-white"><Play className="h-2.5 w-2.5 fill-current" /></span>Watch the agent work</a>
            </Button>
          </motion.div>

          <motion.ul {...enter(0.38)} className="mt-7 flex flex-wrap gap-x-5 gap-y-2">
            {assurances.map(item => (
              <li key={item} className="flex items-center gap-1.5 text-[14px] font-medium text-bv-ink2">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-bv-success-soft text-bv-success"><Check className="h-2.5 w-2.5" strokeWidth={3} /></span>
                {item}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: 40, rotateX: 8 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 1, delay: 0.2, ease: EASE }} style={{ transformPerspective: 1400 }} className="min-w-0">
          <AgentConsole />
        </motion.div>
      </div>
    </section>
  );
}
